import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/store";
import { ClockIcon } from "@/components/icons";
import { formatRelativeVisit } from "./relative-visit-time";

const HEADING_ID = "recent-pages-heading";

/**
 * Backward-navigation shortcut rendered in every `PageHeader`: a pill showing
 * how many other pages the visitor has been on, opening the trail as a menu.
 *
 * `z-20` places the panel in the in-page-popover band — below the shell chrome
 * (z-30) and the modal floor (drawer z-40, dialog z-50), so nothing here can
 * intercept a drawer's or dialog's clicks.
 *
 * Below `sm` the pill and the menu's Clear are at least 44x44, a phone's touch
 * target, the pill as tall as the header's buttons beside it; from `sm` both
 * are the compact controls they always were.
 */
export function RecentPagesChip() {
  const { t } = useTranslation("recentPages");
  const navigate = useNavigate();
  const recentPages = useAppStore((s) => s.recentPages);
  const clearRecentPages = useAppStore((s) => s.clearRecentPages);
  // The `select` is load-bearing, not a refinement: this renders on ~130 pages,
  // and a bare `useRouterState()` would subscribe all of them to every field of
  // the router state instead of the one string that decides what to hide.
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const entries = useMemo(
    () => recentPages.filter((page) => page.pathname !== pathname),
    [recentPages, pathname],
  );

  const [open, setOpen] = useState(false);
  // Frozen at open time so the "3m ago" sublines cannot disagree with each
  // other across re-renders while the menu sits open.
  const [openedAt, setOpenedAt] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  useEffect(() => {
    if (open) itemRefs.current[0]?.focus();
  }, [open]);

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = itemRefs.current.filter((el): el is HTMLButtonElement => el !== null);
    if (items.length === 0) return;
    const current = items.findIndex((el) => el === document.activeElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(current + 1) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(current - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    }
  };

  if (entries.length === 0) return null;

  const shortcut = navigator.platform.startsWith("Mac") ? "⌘K" : "Ctrl+K";

  return (
    <div ref={containerRef} className="relative print:hidden">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("chip.ariaLabel", { count: entries.length })}
        onClick={() => {
          if (open) {
            setOpen(false);
            return;
          }
          setOpenedAt(Date.now());
          setOpen(true);
        }}
        className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-sm text-muted transition-colors hover:bg-gray-50 hover:text-on-surface max-sm:min-h-11 max-sm:min-w-11 max-sm:justify-center dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
      >
        <ClockIcon className="size-4" aria-hidden="true" />
        {entries.length}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 min-w-[16rem] rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center justify-between gap-6 px-3 pb-1 pt-2">
            <p id={HEADING_ID} className="text-xs font-semibold uppercase tracking-wide text-muted">
              {t("popover.title")}
            </p>
            <button
              type="button"
              onClick={clearRecentPages}
              className="text-xs font-medium text-primary hover:underline max-sm:inline-flex max-sm:min-h-11 max-sm:min-w-11 max-sm:items-center max-sm:justify-center"
            >
              {t("popover.clear")}
            </button>
          </div>
          {/* `menu` may only own `menuitem`/`separator` children per ARIA's
              required-owned-elements rule, so the header row and the footer
              hint are siblings, wired in via `aria-labelledby`. */}
          <div role="menu" aria-labelledby={HEADING_ID} onKeyDown={onMenuKeyDown}>
            {entries.map((entry, index) => {
              const visited = formatRelativeVisit(openedAt, entry.visitedAt, t);
              return (
                <button
                  key={entry.pathname}
                  type="button"
                  role="menuitem"
                  tabIndex={index === 0 ? 0 : -1}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  onClick={() => {
                    close();
                    void navigate({ href: entry.path });
                  }}
                  className="flex w-full flex-col items-start px-3 py-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <span className="text-sm text-on-surface">{entry.label}</span>
                  <span className="text-xs text-muted">
                    {entry.section ? `${entry.section} · ${visited}` : visited}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-1 border-t border-gray-200 px-3 pb-1 pt-2 text-xs text-muted dark:border-gray-700">
            {t("popover.hint", { shortcut })}
          </p>
        </div>
      )}
    </div>
  );
}
